import React from "react";
import {
  FaChartBar,
  FaUsers,
  FaUserMd,
  FaCalendarCheck,
  FaBars,
  FaStethoscope,
  FaFlask,
  FaShieldAlt,
  FaClock,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

/* ============================================================
   SIDEBAR ITEM
============================================================ */

const SidebarItem = ({ icon: Icon, label, onClick, active }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    className={`
      group
      relative
      flex
      items-center
      gap-3
      px-3
      py-2.5
      cursor-pointer
      rounded-xl
      transition-all
      duration-200
      select-none

      ${
        active
          ? `
            bg-gradient-to-r
            from-[#0f1f33]
            via-[#1a3a5c]
            to-[#1e4976]
            text-white
            shadow-[0_5px_14px_rgba(15,31,51,0.18)]
          `
          : `
            text-[#5d7187]
            hover:bg-[#edf5fa]
            hover:text-[#163b5c]
          `
      }
    `}
  >
    {/* ========================================================
        ACTIVE INDICATOR
    ======================================================== */}

    {active && (
      <span
        className="
          absolute
          left-0
          top-1/2
          -translate-y-1/2
          h-7
          w-1
          rounded-r-full
          bg-[#0ea5e9]
        "
      />
    )}

    {/* ========================================================
        ICON
    ======================================================== */}

    <div
      className={`
        flex
        h-9
        w-9
        flex-shrink-0
        items-center
        justify-center
        rounded-lg
        transition-all
        duration-200

        ${
          active
            ? `
              border
              border-white/10
              bg-white/10
              text-[#7dd3fc]
            `
            : `
              bg-[#f0f5f9]
              text-[#45617b]
              group-hover:bg-white
              group-hover:text-[#0ea5e9]
            `
        }
      `}
    >
      <Icon className="text-sm" />
    </div>

    {/* ========================================================
        LABEL
    ======================================================== */}

    <span
      className={`
        text-sm
        leading-none

        ${active ? "font-bold text-white" : "font-medium"}
      `}
    >
      {label}
    </span>

    {/* ========================================================
        ACTIVE DOT
    ======================================================== */}

    {active && (
      <span
        className="
          ml-auto
          h-1.5
          w-1.5
          rounded-full
          bg-[#38bdf8]
          shadow-[0_0_8px_rgba(56,189,248,0.7)]
        "
      />
    )}
  </div>
);

/* ============================================================
   SIDEBAR
============================================================ */

const SidebarWithUsers = ({ category, setCategory, registeredUsers }) => {
  const navigate = useNavigate();

  /* ==========================================================
     SLOT MANAGEMENT
     Same routing logic used in DashboardHeader
  ========================================================== */

  const handleManageSlots = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const primaryCategory = (user?.categoryPath?.[0] || "").toLowerCase();

      /* ------------------------------------------------------
         ALTERNATIVE MEDICAL
      ------------------------------------------------------ */

      if (primaryCategory.includes("alternative")) {
        navigate("/alternative-avail-slots");
        return;
      }

      /* ------------------------------------------------------
         DOCTOR
      ------------------------------------------------------ */

      if (primaryCategory.includes("doctor")) {
        if (!user?._id) {
          console.error("❌ User ID missing for doctor slot management");
          return;
        }

        navigate(`/doctor/slots/${user._id}`);
        return;
      }

      /* ------------------------------------------------------
         OTHER MEDICAL
      ------------------------------------------------------ */

      if (
        primaryCategory.includes("other medical") ||
        primaryCategory.includes("medical")
      ) {
        navigate("/availabilities/other-medical/slots");
        return;
      }

      /* ------------------------------------------------------
         FALLBACK
      ------------------------------------------------------ */

      console.warn("⚠️ No slot route found for category:", primaryCategory);

      setCategory("Slot Management");
    } catch (error) {
      console.error("❌ Slot navigation error:", error);
    }
  };

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <aside
      className="
        fixed
        left-0
        top-0
        z-40
        hidden
        h-screen
        w-64
        flex-col
        border-r
        border-[#d8e3ee]
        bg-white
        shadow-[2px_0_15px_rgba(15,31,51,0.06)]
        md:flex
        lg:w-64
        xl:w-72
      "
    >
      {/* =====================================================
          SIDEBAR HEADER
      ===================================================== */}

      <div className="px-4 pb-4 pt-4">
        <div
          className="
            relative
            overflow-hidden
            rounded-2xl
            bg-gradient-to-br
            from-[#0f1f33]
            via-[#1a3a5c]
            to-[#1e4976]
            px-4
            py-4
            shadow-[0_6px_20px_rgba(15,31,51,0.18)]
          "
        >
          {/* Decorative Glow */}

          <div
            className="
              absolute
              -right-8
              -top-8
              h-24
              w-24
              rounded-full
              bg-[#0ea5e9]/20
              blur-xl
            "
          />

          {/* Secondary Glow */}

          <div
            className="
              absolute
              -bottom-10
              -left-10
              h-24
              w-24
              rounded-full
              bg-[#22d3ee]/10
              blur-xl
            "
          />

          <div className="relative flex items-center gap-3">
            {/* LOGO ICON */}

            <div
              className="
                flex
                h-10
                w-10
                items-center
                justify-center
                rounded-xl
                border
                border-white/10
                bg-white/10
                text-[#7dd3fc]
                shadow-sm
              "
            >
              <FaBars className="text-base" />
            </div>

            {/* HEADER TEXT */}

            <div className="min-w-0">
              <p
                className="
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.18em]
                  text-sky-200
                "
              >
                Medical Portal
              </p>

              <h2
                className="
                  mt-0.5
                  text-base
                  font-bold
                  text-white
                "
              >
                Dashboard
              </h2>
            </div>
          </div>

          {/* DESCRIPTION */}

          <div className="relative mt-3">
            <p className="text-[10px] text-slate-300">
              Manage your healthcare network
            </p>
          </div>
        </div>
      </div>

      {/* =====================================================
          NAVIGATION TITLE
      ===================================================== */}

      <div className="px-5 pb-2">
        <p
          className="
            text-[9px]
            font-bold
            uppercase
            tracking-[0.18em]
            text-[#91a1b1]
          "
        >
          Main Navigation
        </p>
      </div>

      {/* =====================================================
          NAVIGATION
      ===================================================== */}

      <nav
        className="
          flex-1
          space-y-1
          overflow-y-auto
          px-3
          py-2

          scrollbar-thin
          scrollbar-thumb-[#cbd9e5]
          scrollbar-track-transparent
        "
      >
        {/* ===================================================
            HOME
        =================================================== */}

        <SidebarItem
          icon={FaChartBar}
          label="Home"
          onClick={() => {
            console.log("🏠 Home clicked");
            setCategory("Home");
          }}
          active={category === "Home"}
        />

        {/* ===================================================
            PATIENTS
        =================================================== */}

        <SidebarItem
          icon={FaUsers}
          label="Patients"
          onClick={() => {
            console.log("🧑‍⚕️ Patients clicked");
            setCategory("Patients");
          }}
          active={category === "Patients"}
        />

        {/* ===================================================
            DOCTORS
        =================================================== */}

        <SidebarItem
          icon={FaUserMd}
          label="Doctors"
          onClick={() => {
            console.log("👨‍⚕️ Doctors clicked");
            setCategory("Doctors");
          }}
          active={category === "Doctors"}
        />

        {/* ===================================================
            OTHER MEDICAL SERVICES
        =================================================== */}

        <SidebarItem
          icon={FaStethoscope}
          label="Other Medical Services"
          onClick={() => {
            console.log("🩺 Other Medical Services clicked");

            setCategory("Other Medical Services");
          }}
          active={category === "Other Medical Services"}
        />

        {/* ===================================================
            APPOINTMENTS
        =================================================== */}

        <SidebarItem
          icon={FaCalendarCheck}
          label="Appointments"
          onClick={() => {
            console.log("📅 Appointments clicked");

            setCategory("Appointments");
          }}
          active={category === "Appointments"}
        />

        {/* ===================================================
            SLOT MANAGEMENT
        =================================================== */}

        <div className="pt-2">
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              console.log("⏰ Slot Management clicked");

              handleManageSlots();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();

                handleManageSlots();
              }
            }}
            className="
              group
              relative
              flex
              w-full
              cursor-pointer
              items-center
              gap-3
              rounded-xl
              border
              border-cyan-200/30
              bg-gradient-to-r
              from-[#0ea5e9]
              via-[#0891b2]
              to-[#06b6d4]
              px-3
              py-2.5
              text-white
              shadow-[0_5px_16px_rgba(14,165,233,0.20)]
              transition-all
              duration-200
              select-none

              hover:-translate-y-0.5
              hover:from-[#0284c7]
              hover:via-[#0e7490]
              hover:to-[#0891b2]
              hover:shadow-[0_7px_20px_rgba(14,165,233,0.30)]

              focus:outline-none
              focus:ring-2
              focus:ring-cyan-400/40
            "
          >
            {/* LEFT INDICATOR */}

            <span
              className="
                absolute
                left-0
                top-1/2
                h-7
                w-1
                -translate-y-1/2
                rounded-r-full
                bg-white
                opacity-90
              "
            />

            {/* SLOT ICON */}

            <div
              className="
                flex
                h-9
                w-9
                flex-shrink-0
                items-center
                justify-center
                rounded-lg
                border
                border-white/10
                bg-white/10
                text-white
                shadow-sm
                backdrop-blur-sm
                transition-transform
                duration-200
                group-hover:scale-110
              "
            >
              <FaClock className="text-sm" />
            </div>

            {/* SLOT TEXT */}

            <div className="min-w-0 flex-1">
              <span
                className="
                  block
                  text-sm
                  font-bold
                  leading-none
                  text-white
                "
              >
                Slot Management
              </span>

              <span
                className="
                  mt-1
                  block
                  truncate
                  text-[9px]
                  font-medium
                  text-cyan-100
                "
              >
                Manage your availability
              </span>
            </div>

            {/* ARROW */}

            <span
              className="
                flex
                h-6
                w-6
                flex-shrink-0
                items-center
                justify-center
                rounded-full
                bg-white/10
                text-xs
                font-bold
                text-white
                transition-all
                duration-200
                group-hover:translate-x-1
                group-hover:bg-white/20
              "
            >
              →
            </span>
          </div>
        </div>

        {/* ===================================================
            DIVIDER
        =================================================== */}

        <div className="py-3">
          <div className="border-t border-[#e5edf4]" />
        </div>
      </nav>

      {/* =====================================================
          SIDEBAR FOOTER
      ===================================================== */}

      <div
        className="
          border-t
          border-[#e1e8f0]
          p-4
        "
      >
        {/* FOOTER STATUS CARD */}

        <div
          className="
            rounded-xl
            border
            border-[#dce7ef]
            bg-gradient-to-r
            from-[#f1f6fa]
            to-[#eef7fb]
            px-3
            py-3
          "
        >
          <div className="flex items-center gap-3">
            {/* STATUS ICON */}

            <div
              className="
                relative
                flex
                h-9
                w-9
                flex-shrink-0
                items-center
                justify-center
                rounded-full
                bg-gradient-to-br
                from-[#1a3a5c]
                to-[#0ea5e9]
                text-white
                shadow-sm
              "
            >
              <FaStethoscope className="text-sm" />

              {/* ONLINE DOT */}

              <span
                className="
                  absolute
                  bottom-0
                  right-0
                  h-2.5
                  w-2.5
                  rounded-full
                  border-2
                  border-white
                  bg-green-400
                "
              />
            </div>

            {/* FOOTER TEXT */}

            <div className="min-w-0">
              <p
                className="
                  truncate
                  text-xs
                  font-bold
                  text-[#193a5c]
                "
              >
                Medical Network
              </p>

              <div className="mt-0.5 flex items-center gap-1">
                <span
                  className="
                    h-1.5
                    w-1.5
                    rounded-full
                    bg-green-400
                  "
                />

                <p
                  className="
                    text-[10px]
                    text-[#7d91a5]
                  "
                >
                  Secure & Active
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* VERSION */}

        <p
          className="
            mt-3
            text-center
            text-[9px]
            text-[#a1afbd]
          "
        >
          MRHealth Medical Portal
        </p>
      </div>
    </aside>
  );
};

export default SidebarWithUsers;
